import { useAppDispatch, useAppSelector } from 'app/hooks';
import FormInput from 'components/common/form-input';
import Button from 'components/inputs/button';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useState } from 'react';
import axios from 'utils/api';
import {
  SizingBandEquipmentType,
  SizingBandRowDraft,
  validateSizingBands
} from 'utils/sizing-bands-validation';
import { errorModal, successModal } from 'utils/ui';

// Fixed row order — the equipment types are a closed enum, so the form
// renders exactly one row per type (no add/remove) and always saves in this
// canonical order.
const EQUIPMENT_TYPES: SizingBandEquipmentType[] = [
  'acCooling',
  'hpCooling',
  'heating'
];

const EQUIPMENT_TYPE_LABELS: Record<SizingBandEquipmentType, string> = {
  acCooling: 'AC cooling',
  hpCooling: 'HP cooling',
  heating: 'Heating'
};

// Shipped defaults — mirrors the migration seed
// (V2026.08.11.140000__sizing_bands_setting.sql; heating lower bound 95 is a
// Geoff ruling). Used only to hydrate the form when the setting is absent
// (older API deploy without the seed migration); Save takes effect only once
// the migration has run, because the settings PATCH endpoint is UPDATE-only.
const DEFAULT_SIZING_BANDS: {
  equipmentType: SizingBandEquipmentType;
  lowerPercent: number;
  upperPercent: number;
}[] = [
  { equipmentType: 'acCooling', lowerPercent: 95, upperPercent: 115 },
  { equipmentType: 'hpCooling', lowerPercent: 100, upperPercent: 125 },
  { equipmentType: 'heating', lowerPercent: 95, upperPercent: 140 }
];

// Self-contained 'Sizing Bands' settings block (AHA-6796): the
// percent-of-load window each equipment type's capacity must land in on the
// ducted filtering path. Replaces the old two-input CCB capacity band form
// (AHA-6681). Self-contained so future settings blocks can slot into the
// page alongside it.
export default function SizingBandsSettings() {
  const dispatch = useAppDispatch();
  const settings = useAppSelector((state) => state.admin.settings);
  const [rows, setRows] = useState<SizingBandRowDraft[]>([]);

  useEffect(() => {
    if (!settings) {
      return;
    }
    const bands = settings.sizingBands ?? DEFAULT_SIZING_BANDS;
    // Hydrate in canonical order regardless of store order, so a reordered
    // or partial stored value can't reorder or drop the fixed rows.
    setRows(
      EQUIPMENT_TYPES.map((equipmentType) => {
        const band = bands.find((b) => b.equipmentType === equipmentType);
        return {
          equipmentType,
          lowerPercent: band?.lowerPercent,
          upperPercent: band?.upperPercent
        };
      })
    );
  }, [settings]);

  const errors = validateSizingBands(rows);
  const hasErrors = Boolean(errors.rows);

  const handleRowChange = (
    index: number,
    key: 'lowerPercent' | 'upperPercent',
    value: number | undefined
  ) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const handleSave = async () => {
    if (hasErrors) {
      return;
    }
    const bands = rows.map((row) => ({
      equipmentType: row.equipmentType,
      lowerPercent: Number(row.lowerPercent),
      upperPercent: Number(row.upperPercent)
    }));
    try {
      await axios.patch('settings', [
        { key: 'sizingBands', value: JSON.stringify(bands) }
      ]);
      dispatch(fetchDefaultAdminData());
      successModal(`Settings updated.`);
    } catch {
      errorModal('Something went wrong.');
    }
  };

  return (
    <div className='flex flex-col gap-5'>
      <div className='text-xl font-bold text-gray-900'>Sizing Bands</div>
      <div className='text-gray-500'>
        Percent-of-load window each equipment type&apos;s capacity must land in
        when filtering ducted systems (e.g. AC cooling 95–115% of the cooling
        requirement).
      </div>
      <div className='flex max-w-2xl flex-col gap-3'>
        {rows.map((row, index) => (
          <div
            key={row.equipmentType}
            className='flex items-start gap-3 rounded-lg bg-white p-3'
          >
            <div className='w-28 pt-9 font-semibold text-gray-900'>
              {EQUIPMENT_TYPE_LABELS[row.equipmentType]}
            </div>
            <div className='flex flex-col gap-1'>
              <FormInput
                type='number'
                label='Lower %'
                value={row.lowerPercent}
                onChange={(value) =>
                  handleRowChange(index, 'lowerPercent', value)
                }
                step={1}
                min={0}
                required
              />
              {errors.rows?.[index]?.lowerPercent && (
                <div className='text-sm text-red-500'>
                  {errors.rows[index].lowerPercent}
                </div>
              )}
            </div>
            <div className='flex flex-col gap-1'>
              <FormInput
                type='number'
                label='Upper %'
                value={row.upperPercent}
                onChange={(value) =>
                  handleRowChange(index, 'upperPercent', value)
                }
                step={1}
                min={0}
                required
              />
              {errors.rows?.[index]?.upperPercent && (
                <div className='text-sm text-red-500'>
                  {errors.rows[index].upperPercent}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div>
        <Button
          text='Save'
          color='green'
          onClick={handleSave}
          disabled={hasErrors}
        />
      </div>
    </div>
  );
}
